import React, { useState } from 'react';
import { Container, Header, Table, Badge, Box, Link, SpaceBetween, Checkbox } from '@amzn/awsui-components-react-v3';
import { StuckRow, TicketGroup, TicketInfo } from './types';
import { STUCK_TICKET_THRESHOLD, WORKFLOW, RAIL } from './constants';
import { buildTicketGroups, bucketUrl, railOf, applyTickets } from './stuckLayout';

interface Props {
    rows: StuckRow[];
    /** Per-(region|rail|plugin) SIM-T enrichment from the backend payload. Absent → search-link fallback. */
    tickets?: TicketInfo[];
}

const breakdown = (byOp: Record<string, number>) =>
    Object.entries(byOp).sort((a, b) => b[1] - a[1]).map(([op, c]) => `${op}: ${c.toLocaleString()}`).join(' · ');

// Scheme allowlist for hrefs sourced from the backend /stuck payload (ticket/search URLs).
// React/Cloudscape do not validate href schemes, so a javascript:/data: URL would execute on
// click. Normalize against an allowlist; anything else (or unparseable) collapses to undefined.
const ALLOWED_URL_SCHEMES = ['https:', 'http:', 'mailto:'];
const safeHref = (raw: string | null | undefined): string | undefined => {
    if (!raw) return undefined;
    try {
        const base = typeof window !== 'undefined' ? window.location.origin : undefined;
        const u = new URL(String(raw), base);
        return ALLOWED_URL_SCHEMES.includes(u.protocol) ? u.href : undefined;
    } catch {
        return undefined;
    }
};

// SIM-T status → Cloudscape badge color.
const statusColor = (status: string | null | undefined): 'green' | 'blue' | 'grey' => {
    const s = (status || '').toLowerCase();
    if (s.includes('resolved') || s.includes('closed')) return 'green';
    if (s.includes('open') || s.includes('assigned') || s.includes('work') || s.includes('research')) return 'blue';
    return 'grey';
};

// Ticket / Action cell: list every matched open SAM ticket (id + status + severity); if none matched,
// a SIM-T search deep-link fallback.
const ticketCell = (g: TicketGroup): React.ReactNode => {
    if (g.ticketFound && g.ticketList && g.ticketList.length > 0) {
        return (
            <SpaceBetween size="xxs" direction="vertical">
                {g.ticketList.map((t, i) => (
                    <SpaceBetween key={(t.ticketId ?? String(i))} size="xxs" direction="horizontal">
                        <Link external href={safeHref(t.ticketUrl)}>{t.ticketId}</Link>
                        {t.ticketStatus ? <Badge color={statusColor(t.ticketStatus)}>{t.ticketStatus}</Badge> : null}
                        {t.ticketSeverity ? <Badge color="red">{t.ticketSeverity}</Badge> : null}
                    </SpaceBetween>
                ))}
            </SpaceBetween>
        );
    }
    // Back-compat: found but no list (older payload) → primary link + count.
    if (g.ticketFound && g.ticketId) {
        return (
            <SpaceBetween size="xxs" direction="horizontal">
                <Link external href={safeHref(g.ticketUrl)}>{g.ticketId}</Link>
                {g.ticketStatus ? <Badge color={statusColor(g.ticketStatus)}>{g.ticketStatus}</Badge> : null}
                {g.ticketSeverity ? <Badge color="red">{g.ticketSeverity}</Badge> : null}
                {g.ticketCount && g.ticketCount > 1 ? (
                    <Box color="text-body-secondary">+{g.ticketCount - 1} more</Box>
                ) : null}
            </SpaceBetween>
        );
    }
    if (g.simSearchUrl) {
        return <Link external href={safeHref(g.simSearchUrl)}>🔍 Search SIM-T</Link>;
    }
    return <Box color="text-status-inactive">—</Box>;
};

const workflowFor = (g: TicketGroup) => (g.rail === RAIL.NATIVE ? WORKFLOW.NATIVE : WORKFLOW.PCES);

// One Orca bucket link per operation in the group: PCES has a distinct errored state per op
// (ClassifyError::Error::<plugin>:Refund / :OverRefund / :Disburse); Native/PMRAS has a single
// bucket. Fixes the prior link that only covered one op's state.
const groupBuckets = (rows: StuckRow[], g: TicketGroup): Array<{ op: string; url: string }> => {
    const wf = workflowFor(g);
    const byOp = new Map<string, string>(); // op -> representative state
    for (const r of rows) {
        if (r.stuck <= 0) continue;
        if (r.region !== g.region || r.plugin !== g.plugin || railOf(r.workflow) !== g.rail) continue;
        const op = r.op && r.op !== '—' ? r.op : 'bucket';
        if (!byOp.has(op)) byOp.set(op, r.state);
    }
    return Array.from(byOp.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([op, state]) => ({ op, url: bucketUrl(g.region, g.plugin, wf, state) }));
};

const TicketVerificationTable: React.FC<Props> = ({ rows, tickets }) => {
    const errored = rows.filter((r) => r.stuck > 0);
    const { over, under } = buildTicketGroups(errored);
    // Merge backend SIM-T enrichment (keyed by region|rail|plugin); unmatched groups get a search-URL fallback.
    applyTickets(over, tickets);
    applyTickets(under, tickets);
    const underStuck = under.reduce((s, x) => s + x.stuck, 0);
    const [showOthers, setShowOthers] = useState(false);
    // "Ticket required" filter (mirrors the local dashboard): show only >threshold groups with no SAM ticket.
    const [missingOnly, setMissingOnly] = useState(false);
    const visibleOver = missingOnly ? over.filter((g) => !g.ticketFound) : over;
    const visibleUnder = missingOnly ? under.filter((g) => !g.ticketFound) : under;
    // Footer toggle/label must reflect what actually expands (visibleUnder), not the unfiltered set.
    const visibleUnderStuck = visibleUnder.reduce((s, x) => s + x.stuck, 0);
    const items = showOthers ? [...visibleOver, ...visibleUnder] : visibleOver;

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    counter={`(${visibleOver.length})`}
                    actions={
                        <Checkbox
                            checked={missingOnly}
                            onChange={({ detail }) => setMissingOnly(detail.checked)}
                        >
                            Only show plugins missing a ticket
                        </Checkbox>
                    }
                    description={`Plugins with a single op > ${STUCK_TICKET_THRESHOLD} errored stuck work items (SAM per-segment; PCES + Native ticketed separately). ${visibleUnder.length} groups ≤ ${STUCK_TICKET_THRESHOLD} (${visibleUnderStuck.toLocaleString()} stuck) summarized.`}
                >
                    🎫 Ticket Verification
                </Header>
            }
        >
            <Table
                variant="borderless"
                items={items}
                footer={visibleUnder.length > 0 ? (
                    <Box textAlign="center">
                        <Link onFollow={(e) => { e.preventDefault(); setShowOthers((v) => !v); }}>
                            {showOthers
                                ? `▾ Hide ${visibleUnder.length} others ≤ ${STUCK_TICKET_THRESHOLD}`
                                : `▸ Show ${visibleUnder.length} others ≤ ${STUCK_TICKET_THRESHOLD} (${visibleUnderStuck.toLocaleString()} stuck) — with bucket links`}
                        </Link>
                    </Box>
                ) : undefined}
                empty={<Box textAlign="center" color="inherit">No plugin groups over threshold.</Box>}
                columnDefinitions={[
                    { id: 'region', header: 'Region', cell: (g) => g.region },
                    { id: 'rail', header: 'Rail', cell: (g) => (
                        g.rail === RAIL.NATIVE ? <Badge color="blue">Native</Badge> : <Badge color="grey">PCES</Badge>
                    ) },
                    { id: 'plugin', header: 'Plugin', cell: (g) => g.plugin },
                    { id: 'stuck', header: 'Stuck', cell: (g) => g.stuck.toLocaleString() },
                    { id: 'breakdown', header: 'Breakdown', cell: (g) => <Box color="text-body-secondary">{breakdown(g.byOp)}</Box> },
                    { id: 'cti', header: 'CTI / Group', cell: (g) => (
                        g.ticketGroup ? <Box>{g.ticketGroup}</Box> : <Box color="text-status-inactive">—</Box>
                    ) },
                    { id: 'ticket', header: 'Ticket / Action', cell: (g) => ticketCell(g) },
                    { id: 'bucket', header: 'Buckets', cell: (g) => {
                        const links = groupBuckets(rows, g);
                        if (!links.length) return <Box color="text-status-inactive">—</Box>;
                        return (
                            <Box>
                                {links.map((b, i) => (
                                    <React.Fragment key={b.op}>
                                        {i > 0 ? ' · ' : ''}
                                        <Link external href={b.url}>{b.op}</Link>
                                    </React.Fragment>
                                ))}
                            </Box>
                        );
                    } },
                ]}
            />
        </Container>
    );
};

export default TicketVerificationTable;
